import { useState } from "react";
import { Link } from "react-router-dom";

type QuestionD = {
  brand: string;
  brandLogo: string;
  model: string;
  correct: string;
  options: string[];
  hint: string;
};

const ALL_QUESTIONS: QuestionD[] = [
  // BMW
  {
    brand: "BMW",
    brandLogo: "/logos/bmw-logo.png",
    model: "430d xDrive",
    correct: "Sèrie 4 · dièsel · nivell de potència mig · tracció total",
    options: [
      "Sèrie 4 · dièsel · nivell de potència mig · tracció total",
      "Sèrie 4 · gasolina · cilindrada 3.0L · tracció total",
      "Sèrie 4 · dièsel · 430 CV exactes · tracció posterior",
      "Sèrie 3 derivat · dièsel · potència alta · 4 rodes",
    ],
    hint: "El '4' = Sèrie 4 (coupé). El '30' NO és cilindrada ni CV: és el nivell de potència mig dins la sèrie. La 'd' = dièsel. xDrive = tracció total de BMW.",
  },
  {
    brand: "BMW",
    brandLogo: "/logos/bmw-logo.png",
    model: "530e xDrive",
    correct: "Sèrie 5 · hybrid endollable (PHEV) · tracció total",
    options: [
      "Sèrie 5 · hybrid endollable (PHEV) · tracció total",
      "Sèrie 5 · gasolina 3.0L · tracció total",
      "Sèrie 5 · totalment elèctric · tracció total",
      "Sèrie 5 · mild hybrid 48V · tracció posterior",
    ],
    hint: "La 'e' minúscula en BMW indica PHEV endollable, NO elèctric pur. Els elèctrics purs de BMW usen la 'i' davant: iX, i4, i7. La 'i' darrere del número (430i, 530i) = gasolina (injection).",
  },
  {
    brand: "BMW",
    brandLogo: "/logos/bmw-logo.png",
    model: "M440i xDrive",
    correct: "Sèrie 4 · M Performance (no M pur) · gasolina alta potència · tracció total",
    options: [
      "Sèrie 4 · M Performance (no M pur) · gasolina alta potència · tracció total",
      "Model M4 · motor gasolina 4.0L · tracció total",
      "Sèrie 4 · M Sport (pack estètic) · gasolina · tracció total",
      "M4 pur de competició · gasolina 40 CV addicionals · tracció total",
    ],
    hint: "M440i ≠ M4. El prefix 'M' davant d'un número de sèrie (M440i, M340i) indica M Performance: motor potenciat però no és el model M pur. El M4 és un vehicle completament independent amb xassís i motor exclusius d'AMG.",
  },
  {
    brand: "BMW",
    brandLogo: "/logos/bmw-logo.png",
    model: "X5 45e xDrive",
    correct: "SUV gran · hybrid endollable (PHEV) · nivell potència alt (≈394 CV totals) · tracció total",
    options: [
      "SUV gran · hybrid endollable (PHEV) · nivell potència alt (≈394 CV totals) · tracció total",
      "SUV gran · gasolina 4.5L · potència alta · tracció total",
      "SUV gran · totalment elèctric · bateria 45 kWh · tracció total",
      "SUV gran · 45 CV elèctrics addicionals · gasolina · tracció total",
    ],
    hint: "En BMW PHEV, la 'e' al final = hybrid endollable. El '45' és el codi de potència combinada (no CV ni kWh). El X5 45e suma gasolina + elèctric fins a ≈394 CV totals.",
  },
  {
    brand: "BMW",
    brandLogo: "/logos/bmw-logo.png",
    model: "i4 M50",
    correct: "Berlina totalment elèctrica equiv. Sèrie 4 · M Performance de màxima potència · 544 CV",
    options: [
      "Berlina totalment elèctrica equiv. Sèrie 4 · M Performance de màxima potència · 544 CV",
      "Sèrie 4 mild hybrid · pack M esportiu · 50 CV elèctric addicional",
      "Berlina elèctrica · M pur de competició (no M Performance)",
      "Berlina elèctrica Sèrie 4 · M Sport (pack estètic) · màxima potència",
    ],
    hint: "El prefix 'i' en iX, i4, i7 = BMW totalment elèctric. L'i4 és la Gran Coupé elèctrica equiv. a la Sèrie 4. 'M50' = M Performance de màxima potència. NO és un M4 pur: és el sostre de la gamma i4.",
  },
  {
    brand: "BMW",
    brandLogo: "/logos/bmw-logo.png",
    model: "218d Gran Coupé",
    correct: "Sèrie 2 · dièsel · potència baixa-mitja · Gran Coupé = 4 portes tracció davantera (FWD)",
    options: [
      "Sèrie 2 · dièsel · potència baixa-mitja · Gran Coupé = 4 portes tracció davantera (FWD)",
      "Sèrie 2 · gasolina · 218 CV exactes · coupé 2 portes",
      "Derivat Sèrie 1 · dièsel · Gran Coupé = versió familiar 5 portes",
      "Sèrie 2 · dièsel · Gran Coupé = carrosseria familiar (Touring)",
    ],
    hint: "La Sèrie 2 Gran Coupé és una de les poques BMW de tracció davantera (FWD), inusual a la marca. El '18' = nivell de potència baix-mig, 'd' = dièsel. Gran Coupé = 4 portes amb teulada inclinada, ni 2 portes ni familiar.",
  },
  // Mercedes-Benz
  {
    brand: "Mercedes-Benz",
    brandLogo: "/logos/mercedes-benz-logo.png",
    model: "E 220 d",
    correct: "Classe E (berlina executiva) · dièsel · rang de potència baix-mig de la gamma",
    options: [
      "Classe E (berlina executiva) · dièsel · rang de potència baix-mig de la gamma",
      "Classe E · gasolina 2.2L exactes · versió base sense equipament",
      "Berlina executiva · dièsel · exactament 220 CV",
      "Classe E · dièsel · versió de màxima potència de la gamma 'd'",
    ],
    hint: "A Mercedes, el número (220, 300, 450...) NO indica CV ni cilindrada exactes: és un rang de potència relatiu. El E 220 d fa ≈194 CV, no 220. La 'd' minúscula = dièsel. 'D' majúscula seria el model D elèctric (Classe D, però no existeix).",
  },
  {
    brand: "Mercedes-Benz",
    brandLogo: "/logos/mercedes-benz-logo.png",
    model: "GLC 300 e 4MATIC",
    correct: "SUV equiv. Classe C · potència alta de gamma · PHEV endollable · tracció total permanent",
    options: [
      "SUV equiv. Classe C · potència alta de gamma · PHEV endollable · tracció total permanent",
      "SUV gran equiv. Classe E · gasolina 3.0L · tracció total",
      "SUV compacte · totalment elèctric · 300 CV · tracció total",
      "SUV equiv. Classe C · mild hybrid · 300 CV exactes · tracció total",
    ],
    hint: "GLC = SUV basat en la Classe C. El '300' és un codi relatiu alt dins la gamma GLC. La 'e' minúscula a Mercedes = PHEV endollable. 4MATIC = tracció total permanent.",
  },
  {
    brand: "Mercedes-Benz",
    brandLogo: "/logos/mercedes-benz-logo.png",
    model: "AMG C 63 S E Performance",
    correct: "Classe C AMG extrem · motor 4C turbo + elèctric · S = màxima potència · E Performance = PHEV AMG d'alta potència (680 CV)",
    options: [
      "Classe C AMG extrem · motor 4C turbo + elèctric · S = màxima potència · E Performance = PHEV AMG d'alta potència (680 CV)",
      "Classe C AMG pack estètic · motor V8 6.3L original · S = Sport · gasolina pura",
      "Classe C AMG entry · 6.3L V8 · S = Summer performance pack",
      "Classe C AMG · 63 CV elèctric addicional · S = màxima velocitat · elèctric pur",
    ],
    hint: "Originàriament el 63 AMG = V8 6.3L. L'actual C 63 és un 4 cilindres turbo + motor elèctric PHEV de 680 CV totals. S = versió màxima dins el 63. E Performance = el programa PHEV d'altíssima potència d'AMG.",
  },
  {
    brand: "Mercedes-Benz",
    brandLogo: "/logos/mercedes-benz-logo.png",
    model: "EQE 350+",
    correct: "Berlina elèctrica pura equiv. Classe E · motor posterior (RWD) · versió amb autonomia ampliada (+)",
    options: [
      "Berlina elèctrica pura equiv. Classe E · motor posterior (RWD) · versió amb autonomia ampliada (+)",
      "Berlina Classe E · 350 CV exactes · tracció total · versió plus superior",
      "SUV elèctric equiv. Classe E · 350 km autonomia garantida · tracció posterior",
      "Coupé elèctric Classe E · tracció total · + indica PHEV endollable",
    ],
    hint: "EQ = gamma elèctrica pura de Mercedes. EQE = berlina elèctrica equiv. a la Classe E. El '+' = versió de major autonomia (NO tracció total, que s'indica amb '4MATIC'). El 350 és un codi de potència, no CV ni kWh.",
  },
  {
    brand: "Mercedes-Benz",
    brandLogo: "/logos/mercedes-benz-logo.png",
    model: "C 300 de 4MATIC",
    correct: "Classe C · potència alta de gamma · dièsel + PHEV endollable (combo 'de') · tracció total",
    options: [
      "Classe C · potència alta de gamma · dièsel + PHEV endollable (combo 'de') · tracció total",
      "Classe C · gasolina 3.0L · hybrid dièsel-elèctric mild · tracció total",
      "Classe C · exactament 300 CV · dièsel d'alt rendiment · tracció posterior",
      "Classe C · potència mitja · dièsel mild hybrid 48V · tracció total opcional",
    ],
    hint: "La combinació 'de' = dièsel ('d') + PHEV endollable ('e'), un dels combos de motor peculiars de Mercedes. El '300' indica potència alta de gamma, no 300 CV exactes. 4MATIC = tracció total.",
  },
  // Audi
  {
    brand: "Audi",
    brandLogo: "/logos/audi-logo.png",
    model: "A6 55 TFSI quattro",
    correct: "Berlina executiva · codi potència 55 (≈340 CV) · gasolina turbo injecció directa · tracció integral quattro",
    options: [
      "Berlina executiva · codi potència 55 (≈340 CV) · gasolina turbo injecció directa · tracció integral quattro",
      "Berlina executiva · motor 5.5L gasolina · tracció integral",
      "Berlina executiva · 55 CV elèctric addicional · gasolina hybrid · quattro",
      "A6 · edició 55è aniversari de la marca · TFSI gasolina turbo · tracció integral",
    ],
    hint: "Audi usa codis numèrics de potència: 30 (≈116 CV), 35 (≈150), 40 (≈204), 45 (≈245), 50 (≈286), 55 (≈340), 60 (≈435). Res a veure amb cilindrada. TFSI = gasolina turbo amb injecció directa.",
  },
  {
    brand: "Audi",
    brandLogo: "/logos/audi-logo.png",
    model: "Q5 40 TDI quattro",
    correct: "SUV mida mitja · codi potència 40 (≈204 CV) · dièsel turbo injecció directa · tracció integral quattro",
    options: [
      "SUV mida mitja · codi potència 40 (≈204 CV) · dièsel turbo injecció directa · tracció integral quattro",
      "SUV gran · motor 4.0L dièsel · tracció integral quattro",
      "SUV coupé gran · dièsel · 40 km autonomia elèctrica · quattro tracció total",
      "SUV mida mitja · dièsel · 40 CV addicionals vs gasolina · quattro",
    ],
    hint: "El '40' és el codi de potència Audi de gamma mitja-baixa (≈204 CV, no 4.0L). TDI = Turbocharged Direct Injection = dièsel turbo. quattro = tracció integral permanent d'Audi.",
  },
  {
    brand: "Audi",
    brandLogo: "/logos/audi-logo.png",
    model: "RS3 Sportback",
    correct: "Versió Rennsport (màxima potència) de l'A3 · motor 5 cilindres en línia 400 CV · carrosseria hatchback 5 portes",
    options: [
      "Versió Rennsport (màxima potència) de l'A3 · motor 5 cilindres en línia 400 CV · carrosseria hatchback 5 portes",
      "Versió S esportiva (un nivell sota RS) de l'A3 · Sportback = 3 portes esportiu",
      "A3 amb pack RS estètic (no mecànic) · carrosseria sedan 4 portes",
      "Model RS autònom de la marca · Sèrie 3 d'Audi · Sportback = descapotable",
    ],
    hint: "A Audi: S = versió esportiva (1r nivell), RS = Rennsport = màxima potència (2n nivell). El RS3 usa un motor 5 cilindres en línia de 400 CV únic i salvatge. Sportback = fastback 5 portes (ni sedan ni descapotable).",
  },
  {
    brand: "Audi",
    brandLogo: "/logos/audi-logo.png",
    model: "SQ5 TDI",
    correct: "Versió S esportiva del SUV Q5 · dièsel 341 CV · un nivell sota RS (si existís RS Q5)",
    options: [
      "Versió S esportiva del SUV Q5 · dièsel 341 CV · un nivell sota RS (si existís RS Q5)",
      "Versió RS Rennsport del Q5 · dièsel turbo · màxima potència de la gamma Q5",
      "Q5 amb pack S estètic (sense millores mecàniques) · dièsel",
      "Model S autònom de la marca · Q5 = carrosseria · dièsel turbo",
    ],
    hint: "S davant d'un model Audi (SQ5, S3, S6...) = versió esportiva genuïna amb motor millorat, NO pack estètic. RS seria el nivell superior (no hi ha RS Q5 però sí RS Q8). El SQ5 TDI usa un V6 dièsel de 341 CV.",
  },
  {
    brand: "Audi",
    brandLogo: "/logos/audi-logo.png",
    model: "e-tron GT RS",
    correct: "Gran turisme totalment elèctric · RS = Rennsport màxima potència (646 CV) · plataforma compartida amb Porsche Taycan",
    options: [
      "Gran turisme totalment elèctric · RS = Rennsport màxima potència (646 CV) · plataforma compartida amb Porsche Taycan",
      "SUV e-tron elèctric · GT = Gran Touring pack estètic · RS = versió Racing Sport",
      "A7 totalment elèctric · GT = versió de circuit pura · RS = versió entry de l'e-tron GT",
      "Gran turisme hybrid · RS = versió esportiva S de l'e-tron · 4 cilindres turbo",
    ],
    hint: "L'e-tron GT és un gran turisme baix i esportiu (NO un SUV) totalment elèctric. RS = Rennsport, màxima potència amb 646 CV. Curiós: comparteix plataforma J1 amb el Porsche Taycan, ambdós del Grup Volkswagen.",
  },
  // Porsche
  {
    brand: "Porsche",
    brandLogo: "/logos/porsche-logo.png",
    model: "911 Carrera 4S",
    correct: "911 coupé · Carrera = variant de base de la família · 4 = tracció total · S = versió de major potència (≈450 CV)",
    options: [
      "911 coupé · Carrera = variant de base de la família · 4 = tracció total · S = versió de major potència (≈450 CV)",
      "911 coupé · Carrera 4 = motor de 4 cilindres · S = Sport pack de màxima potència",
      "911 de 4a generació · S = Sport series · tracció posterior exclusiva",
      "911 descapotable · 4 rodes tracció total · S = cim de gamma · Carrera = variant estàndard",
    ],
    hint: "Al 911: Carrera = variant base. El '4' afegit = tracció total (sense '4' = tracció posterior). 'S' = versió de major potència que el Carrera estàndard (+50-80 CV aprox). Jerarquia: Carrera → S → GTS → Turbo → Turbo S → GT3 → GT3 RS.",
  },
  {
    brand: "Porsche",
    brandLogo: "/logos/porsche-logo.png",
    model: "Macan T",
    correct: "SUV compacte Porsche · T = Touring · versió dinàmica de carretera entry-sport (no és Turbo)",
    options: [
      "SUV compacte Porsche · T = Touring · versió dinàmica de carretera entry-sport (no és Turbo)",
      "SUV compacte Porsche · T = Turbo · la versió de màxima potència de la gamma Macan",
      "SUV compacte Porsche · T = Targa · versió especial amb obertura de sostre",
      "SUV compacte Porsche · T = Technology pack · versió amb màxim equipament tecnològic",
    ],
    hint: "'T' a Porsche NO és Turbo. Significa Touring: orientació a conducció dinàmica de carretera, entre el model base i el GTS. El Macan Turbo (màxima potència) es diu directament 'Turbo'. No et deixis enganyar.",
  },
  {
    brand: "Porsche",
    brandLogo: "/logos/porsche-logo.png",
    model: "Cayenne Turbo S E-Hybrid",
    correct: "SUV Porsche · Turbo S = cim de la jerarquia tèrmica · E-Hybrid = PHEV endollable · ≈680 CV totals",
    options: [
      "SUV Porsche · Turbo S = cim de la jerarquia tèrmica · E-Hybrid = PHEV endollable · ≈680 CV totals",
      "SUV Porsche · Turbo = motor amb turbo bàsic · S = Sport pack estètic · E-Hybrid = mild hybrid 12V",
      "SUV elèctric Porsche · S = versió màxima · E-Hybrid = motor auxiliar gasolina de suport",
      "SUV Porsche · Turbo S = versió tèrmica pura màxima · E = versió elèctrica futura",
    ],
    hint: "Jerarquia del Cayenne: base → S → GTS → Turbo → Turbo S → Turbo S E-Hybrid. El Turbo S E-Hybrid és el cim absolut: V8 biturbo + motor elèctric PHEV = ≈680 CV. E-Hybrid = PHEV endollable, no mild hybrid.",
  },
  {
    brand: "Porsche",
    brandLogo: "/logos/porsche-logo.png",
    model: "718 Cayman GT4 RS",
    correct: "Coupé mig motor atmosfèric · GT4 = màxima versió circuit · RS = Rennsport, la variant més lleugera i extrema",
    options: [
      "Coupé mig motor atmosfèric · GT4 = màxima versió circuit · RS = Rennsport, la variant més lleugera i extrema",
      "Roadster 718 (Boxster) · GT4 = 4a generació GT · RS = Racing Sport pack",
      "Coupé 718 · GT4 = Gran Touring 4 cilindres · RS = Racing Sport estàndard",
      "718 descapotable · GT = Gran Touring · 4RS = tracció total de 4 rodes RS",
    ],
    hint: "718 Cayman GT4 RS: coupé de mig motor, motor F6 4.0L atmosfèric de 500 CV sense turbo. GT4 = versió de circuit pura del Cayman. RS = Rennsport, la variant ancora més extrema i lleugera. Probablement el cotxe de carretera atmosfèric més pur de Porsche.",
  },
  {
    brand: "Porsche",
    brandLogo: "/logos/porsche-logo.png",
    model: "Taycan 4 Cross Turismo",
    correct: "Berlina/familiar elèctrica · 4 = tracció total (no Turbo) · Cross Turismo = carrosseria familiar amb terra lleugerament elevat",
    options: [
      "Berlina/familiar elèctrica · 4 = tracció total (no Turbo) · Cross Turismo = carrosseria familiar amb terra lleugerament elevat",
      "Taycan de 4 cilindres elèctric · Cross = plataforma alta · Turismo = versió berlina estàndard",
      "Taycan entrada de gamma · 4 = 4 portes de sèrie · Cross Turismo = variant coupé 4 portes",
      "Taycan 4a generació · Cross = tracció total especial · Turismo = gran turisme tancat",
    ],
    hint: "El Taycan '4' (sense Turbo ni Turbo S) = tracció total però en rang de potència mig. Cross Turismo = carrosseria familiar (Sport Turismo = la versió asfalt, Cross Turismo = terra lleugerament elevat). Similar al Panamera Sport Turismo.",
  },
  // Altres
  {
    brand: "Alfa Romeo",
    brandLogo: "/logos/alfa-romeo-logo.png",
    model: "Giulia 2.0T Q4",
    correct: "Berlina mitja · motor gasolina turbo 2.0L · Q4 = tracció total (no és la versió Quadrifoglio)",
    options: [
      "Berlina mitja · motor gasolina turbo 2.0L · Q4 = tracció total (no és la versió Quadrifoglio)",
      "Berlina mitja · 2.0L turbo · Q4 = versió Quadrifoglio de màxima potència",
      "SUV Giulia · turbo 4 cilindres · Q4 = quarta generació Quadrifoglio",
      "Berlina Giulia · V4 turbo · Q4 = el màxim de potència d'Alfa Romeo",
    ],
    hint: "A l'Alfa Romeo Giulia, Q4 = tracció total en quatre rodes (no la versió de competició). El nom complet de la versió esportiva extrema és 'Giulia Quadrifoglio' (sense el Q4). La Giulia Q4 és la 2.0T gasolina amb tracció total.",
  },
  {
    brand: "Volvo",
    brandLogo: "/logos/volvo-logo.png",
    model: "XC90 Recharge T8 AWD",
    correct: "SUV gran 7 places · Recharge = gamma electrificada · T8 = PHEV gasolina+elèctric (≈455 CV totals) · AWD = tracció total",
    options: [
      "SUV gran 7 places · Recharge = gamma electrificada · T8 = PHEV gasolina+elèctric (≈455 CV totals) · AWD = tracció total",
      "SUV gran · Recharge = totalment elèctric pur · T8 = 8a generació del model · AWD tracció total",
      "SUV gran · Recharge = mild hybrid 48V · T8 = motor de 8 cilindres turbo · tracció total",
      "SUV mida mitja · PHEV · T8 = nivell de potència 8è de la gamma · AWD opcional",
    ],
    hint: "A Volvo, Recharge = tots els vehicles electrificats (PHEV i BEV). T8 = PHEV d'alta potència (motor gasolina + motor elèctric). T6 = PHEV menys potent. B5/B6/B8 = mild hybrid 48V. Els BEV purs = Pure Electric (ex: XC40 Recharge Pure Electric).",
  },
  {
    brand: "CUPRA",
    brandLogo: "/logos/cupra-logo.png",
    model: "Formentor VZ5",
    correct: "Primer model exclusiu de CUPRA · VZ = Veloz (màxima potència CUPRA) · 5 = motor 5 cilindres en línia d'Audi de 390 CV",
    options: [
      "Primer model exclusiu de CUPRA · VZ = Veloz (màxima potència CUPRA) · 5 = motor 5 cilindres en línia d'Audi de 390 CV",
      "SEAT Formentor versió CUPRA · VZ = Versió Z esportiva · 5 = 5a generació del model",
      "SUV CUPRA compacte · VZ = Velocitat Zero (elèctric) · 5 = 5 portes de sèrie",
      "Formentor CUPRA base · VZ = Versió Zinco exclusiva · 5 = potència de 500 CV",
    ],
    hint: "El Formentor = primer model dissenyat exclusivament per a CUPRA (no és un SEAT). VZ = Veloz, la màxima potència de la gamma Formentor. VZ5 = usa el motor 5 cilindres 2.5L d'Audi RS3/TT RS de 390 CV, extraordinàriament inusual en un SUV.",
  },
  {
    brand: "Mercedes-Benz",
    brandLogo: "/logos/mercedes-benz-logo.png",
    model: "GLA 250 4MATIC",
    correct: "SUV compacte (basat en Classe A) · potència mitja-alta de gamma · tracció total permanent",
    options: [
      "SUV compacte (basat en Classe A) · potència mitja-alta de gamma · tracció total permanent",
      "Berlina Classe A · exactament 250 CV · tracció total permanent",
      "SUV gran de luxe · motor gasolina 2.5L · tracció total permanent",
      "Coupé compacte GL · '250' = versió Sport · tracció total permanent",
    ],
    hint: "GLA = el SUV elevat de la Classe A. GL + lletra = versió SUV d'aquella Classe: GLA (A), GLB (B), GLC (C), GLE (E), GLS (S). El '250' NO indica 250 CV (fa ≈224 CV). 4MATIC = tracció total.",
  },
  {
    brand: "Audi",
    brandLogo: "/logos/audi-logo.png",
    model: "Q7 60 TFSI e quattro",
    correct: "SUV gran 7 places · codi potència 60 (≈462 CV totals) · PHEV gasolina endollable · tracció integral quattro",
    options: [
      "SUV gran 7 places · codi potència 60 (≈462 CV totals) · PHEV gasolina endollable · tracció integral quattro",
      "SUV gran · motor 6.0L gasolina · totalment elèctric · tracció integral quattro",
      "SUV gran · 60 km d'autonomia elèctrica garantida · gasolina auxiliar · quattro",
      "SUV gran · codi 60 = versió màxima · TFSI gasolina pura · sense tracció total",
    ],
    hint: "A l'Audi, el codi '60' = ≈462 CV. TFSI + 'e' = PHEV gasolina endollable (el 'e' darrere de TFSI/TDI indica PHEV). quattro = tracció integral. El Q7 60 TFSI e quattro combina motor gasolina + elèctric per a ≈462 CV totals.",
  },
];

const TOTAL = 20;

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const generateQuestions = (): QuestionD[] =>
  shuffle(ALL_QUESTIONS)
    .slice(0, TOTAL)
    .map((q) => ({ ...q, options: shuffle(q.options) }));

const LABELS = ["A", "B", "C", "D"];

export const TestDecodifica = () => {
  const [questions, setQuestions] = useState<QuestionD[]>(generateQuestions);
  const [current, setCurrent] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const question = questions[current];
  const answered = selected !== null;

  const handleSelect = (option: string) => {
    if (answered) return;
    setSelected(option);
    if (option === question.correct) setScore((s) => s + 1);
  };

  const handleNext = () => {
    if (current + 1 >= TOTAL) {
      setFinished(true);
    } else {
      setCurrent((c) => c + 1);
      setSelected(null);
    }
  };

  const handleRetry = () => {
    setQuestions(generateQuestions());
    setCurrent(0);
    setSelected(null);
    setScore(0);
    setFinished(false);
  };

  if (finished) {
    const pct = Math.round((score / TOTAL) * 100);
    const { badge, color } =
      pct === 100
        ? { badge: "Mestre dels models", color: "#eab308" }
        : pct >= 85
        ? { badge: "Expert", color: "#22c55e" }
        : pct >= 65
        ? { badge: "Entès", color: "#3b82f6" }
        : pct >= 40
        ? { badge: "Aficionat", color: "#f97316" }
        : { badge: "Principiant", color: "#6b7280" };

    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-2xl">
          <div className="mb-8">
            <Link
              to="/guia-models/test"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="m15 18-6-6 6-6" />
              </svg>
              Tornar als tests
            </Link>
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 text-center">
            <div className="text-5xl mb-5">
              {pct === 100 ? "🏆" : pct >= 85 ? "🌟" : pct >= 65 ? "👍" : pct >= 40 ? "📚" : "🔍"}
            </div>
            <h2 className="text-2xl font-bold mb-1 text-foreground">Resultat final</h2>
            <p className="text-sm text-muted-foreground mb-4">Desxifra el model</p>
            <div className="text-6xl font-bold mb-1 text-foreground mt-2">
              {score}
              <span className="text-3xl text-muted-foreground">/{TOTAL}</span>
            </div>
            <div className="text-base font-semibold mt-2 mb-7" style={{ color }}>
              {badge}
            </div>
            <div className="w-full bg-secondary rounded-full h-2 mb-8 overflow-hidden">
              <div
                className="h-2 rounded-full transition-all duration-700"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={handleRetry}
                className="px-6 py-3 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity"
              >
                Tornar a intentar
              </button>
              <Link
                to="/guia-models/test"
                className="px-6 py-3 bg-card border border-border text-foreground rounded-xl font-semibold text-sm hover:bg-secondary transition-colors text-center"
              >
                Altres tests
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-2xl">
        {/* Back link */}
        <div className="mb-6">
          <Link
            to="/guia-models/test"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
            Tornar als tests
          </Link>
        </div>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1.5">
            Desxifra el model
          </h1>
          <p className="text-muted-foreground text-sm sm:text-base">
            Tria la interpretació correcta del model. {TOTAL} preguntes.
          </p>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs text-muted-foreground font-medium">
              Pregunta {current + 1} de {TOTAL}
            </span>
            <span className="text-xs font-bold text-foreground">
              {score} correctes
            </span>
          </div>
          <div className="w-full bg-secondary rounded-full h-1.5 overflow-hidden">
            <div
              className="h-1.5 rounded-full bg-foreground transition-all duration-300"
              style={{ width: `${(current / TOTAL) * 100}%` }}
            />
          </div>
        </div>

        {/* Question card */}
        <div className="bg-card border border-border rounded-2xl overflow-hidden">
          {/* Brand + model */}
          <div className="px-6 py-7 text-center border-b border-border">
            {/* Brand chip */}
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-secondary border border-border mb-5">
              <div className="w-6 h-6 rounded-md overflow-hidden bg-white border border-black/10 dark:border-white/15 flex items-center justify-center p-0.5 shrink-0">
                <img
                  src={question.brandLogo}
                  alt={question.brand}
                  className="w-full h-full object-contain"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <span className="text-sm font-bold text-foreground">{question.brand}</span>
            </div>

            {/* Model name */}
            <div className="block">
              <p className="text-[11px] font-bold text-muted-foreground uppercase tracking-widest mb-3">
                Quin és el significat correcte?
              </p>
              <div className="inline-block px-6 py-3.5 rounded-xl bg-secondary border border-border">
                <span className="text-xl sm:text-2xl font-bold font-mono text-foreground tracking-tight">
                  {question.model}
                </span>
              </div>
            </div>
          </div>

          {/* Options */}
          <div className="p-5 sm:p-6">
            <div className="space-y-2.5">
              {question.options.map((option, idx) => {
                let cls =
                  "w-full text-left p-4 rounded-xl border font-medium text-sm transition-all flex items-start gap-3 ";

                if (!answered) {
                  cls += "border-border hover:border-foreground/40 hover:bg-secondary cursor-pointer";
                } else if (option === question.correct) {
                  cls += "border-green-500 bg-green-500/10 text-green-600 dark:text-green-400 cursor-default";
                } else if (option === selected) {
                  cls += "border-red-500 bg-red-500/10 text-red-600 dark:text-red-400 cursor-default";
                } else {
                  cls += "border-border opacity-35 cursor-default";
                }

                return (
                  <button
                    key={idx}
                    className={cls}
                    onClick={() => handleSelect(option)}
                    disabled={answered}
                  >
                    <span className="text-[11px] font-bold shrink-0 w-5 h-5 rounded-md bg-border/60 flex items-center justify-center mt-0.5 font-mono">
                      {LABELS[idx]}
                    </span>
                    <span className="leading-relaxed flex-1">{option}</span>
                    {answered && option === question.correct && (
                      <svg className="shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                    {answered && option === selected && option !== question.correct && (
                      <svg className="shrink-0 mt-0.5" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6 6 18" /><path d="m6 6 12 12" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Hint */}
            {answered && (
              <div className="mt-4 flex gap-3 p-4 bg-secondary/60 border border-border rounded-xl">
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5 text-muted-foreground">
                  <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
                </svg>
                <p className="text-sm text-muted-foreground leading-relaxed">{question.hint}</p>
              </div>
            )}

            {/* Next button */}
            {answered && (
              <button
                onClick={handleNext}
                className="mt-3 w-full py-3 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity"
              >
                {current + 1 >= TOTAL ? "Veure resultats" : "Següent pregunta →"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
